import React from 'react'
import PropTypes from 'prop-types'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import L from 'leaflet'

import PropertyIcon from 'LeafletMap/PropertyIcon'

class PropertyIcons extends React.Component {
  constructor(props) {
    super(props)
    this.markers = []
    this.sharedIcon = null
    this.createMarkersTimeout = null
    this.batchTimeoutId = null
    this.markerLoadGeneration = 0
    this.isProcessing = false
    this.state = {
      isCreatingMarkers: false
    }
  }

  getLatLng(result) {
    if (result.latitude && result.longitude) {
      return [result.latitude, result.longitude]
    }
  }

  componentDidMount() {
    if (this.props.iconConfig === 'MULTIPLE' && this.props.visible && this.props.results.length > 0) {
      this.createMarkers()
    }
  }

  notifyMarkersLoading(isLoading) {
    if (this.props.onMarkersLoadingChange) {
      this.props.onMarkersLoadingChange(isLoading)
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.iconConfig === 'MULTIPLE' && this.props.visible &&
      (prevProps.results !== this.props.results ||
        prevProps.visible !== this.props.visible ||
        prevProps.clusterGroup !== this.props.clusterGroup)) {
      // Cancel any pending marker creation
      if (this.createMarkersTimeout) {
        clearTimeout(this.createMarkersTimeout)
      }
      this.createMarkersTimeout = setTimeout(() => {
        // Create clustered markers for any results (clustering threshold is handled by minClusterSize)
        this.createMarkers()
      }, 200) // Increased debounce time
    }
  }

  stopMarkerCreation() {
    this.markerLoadGeneration += 1
    if (this.createMarkersTimeout) {
      clearTimeout(this.createMarkersTimeout)
      this.createMarkersTimeout = null
    }
    if (this.batchTimeoutId) {
      clearTimeout(this.batchTimeoutId)
      this.batchTimeoutId = null
    }
    this.isProcessing = false
    this.setState({ isCreatingMarkers: false })
    this.clearMarkers()
    this.notifyMarkersLoading(false)
  }

  componentWillUnmount() {
    this.stopMarkerCreation()
  }

  clearMarkers() {
    if (this.props.clusterGroup && this.markers.length > 0) {
      // Batch remove markers for better performance
      this.props.clusterGroup.removeLayers(this.markers)
      this.markers = []
    }
  }

  createMarkers() {
    if (this.isProcessing) {
      this.stopMarkerCreation()
    }

    const generation = this.markerLoadGeneration + 1
    this.markerLoadGeneration = generation
    this.isProcessing = true
    this.setState({ isCreatingMarkers: true })

    // Clear existing markers first
    this.clearMarkers()

    if (!this.props.results || !Array.isArray(this.props.results)) {
      this.setState({ isCreatingMarkers: false })
      this.isProcessing = false
      this.notifyMarkersLoading(false)
      return
    }

    if (!this.props.results.length) {
      this.setState({ isCreatingMarkers: false })
      this.isProcessing = false
      this.notifyMarkersLoading(false)
      return
    }

    this.notifyMarkersLoading(true)

    // Wait for cluster group to be available
    if (!this.props.clusterGroup) {
      // Retry after a short delay
      setTimeout(() => {
        this.isProcessing = false
        this.setState({ isCreatingMarkers: false })
        if (this.props.clusterGroup) {
          this.createMarkers()
        } else {
          this.notifyMarkersLoading(false)
        }
      }, 100)
      return
    }

    // Create shared icon once for better performance
    if (!this.sharedIcon) {
      const pinSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="20" height="30">
  <path d="M12 0C5.373 0 0 5.373 0 12c0 9.6 12 24 12 24S24 21.6 24 12C24 5.373 18.627 0 12 0z" fill="#1465b7" stroke="rgba(0,0,0,0.3)" stroke-width="1"/>
  <circle cx="12" cy="11" r="4" fill="white" opacity="0.85"/>
</svg>`
      this.sharedIcon = L.divIcon({
        html: pinSvg,
        className: 'property-pin',
        iconSize: [20, 30],
        iconAnchor: [10, 30],
      })
    }

    // Progressive loading for large datasets
    const totalResults = this.props.results.length
    const batchSize = 500 // Process 500 markers at a time
    let currentIndex = 0

    const processBatch = () => {
      if (generation !== this.markerLoadGeneration) {
        return
      }

      if (currentIndex >= totalResults) {
        this.setState({ isCreatingMarkers: false })
        this.isProcessing = false
        this.notifyMarkersLoading(false)
        return
      }

      const endIndex = Math.min(currentIndex + batchSize, totalResults)
      const batchResults = this.props.results.slice(currentIndex, endIndex)
      const markersToAdd = []

      batchResults.forEach((result) => {
        const latLng = this.getLatLng(result)
        if (latLng) {
          // Create minimal marker for better performance
          const marker = L.marker(latLng, {
            icon: this.sharedIcon,
            title: result.address || 'Property' // Simple tooltip instead of popup
          })

          // Add click handler
          marker.on('click', () => {
            if (this.props.handlePropertyAction) {
              this.props.handlePropertyAction(result)
            }
          })

          this.markers.push(marker)
          markersToAdd.push(marker)
        }
      })

      // Add batch to cluster group
      if (markersToAdd.length > 0) {
        this.props.clusterGroup.addLayers(markersToAdd)
      }

      currentIndex = endIndex

      // Process next batch with a small delay to prevent freezing
      if (currentIndex < totalResults) {
        this.batchTimeoutId = setTimeout(processBatch, 10)
      } else {
        this.props.clusterGroup.bringToFront()
        this.setState({ isCreatingMarkers: false })
        this.isProcessing = false
        this.notifyMarkersLoading(false)
      }
    }

    // Start processing
    processBatch()
  }

  render() {
    if (!(this.props.visible && (this.props.results.length || Object.keys(this.props.results).length))) return null

    if (this.props.iconConfig === 'MULTIPLE') {
      // For multiple properties, clustering is handled manually
      // Return null since markers are created in componentDidMount/componentDidUpdate
      return null
    } else {
      // Single property - no clustering needed
      return (
        !!this.props.results.length ||
        (!!Object.keys(this.props.results).length && this.getLatLng(this.props.results) ? (
          <PropertyIcon interactive={false} result={this.props.results} position={this.getLatLng(this.props.results)} />
        ) : null)
      )
    }
  }
}

PropertyIcons.defaultProps = {
  results: [],
}

PropertyIcons.propTypes = {
  handlePropertyAction: PropTypes.func,
  dispatch: PropTypes.func,
  results: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  visible: PropTypes.bool,
  page: PropTypes.string,
  clusterGroup: PropTypes.object,
  onMarkersLoadingChange: PropTypes.func,
}

export default PropertyIcons
